// ProtectedRoute - Route wrapper that requires authentication and/or permissions

import type { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import type { Permission } from '../types';

export interface ProtectedRouteProps {
  children: ReactNode;
  /** Required permissions (user must have ALL of these) */
  requiredPermissions?: Permission[];
  /** Alternative permissions (user must have AT LEAST ONE of these) */
  anyPermission?: Permission[];
  /** Redirect path when not authenticated (default: /login) */
  loginPath?: string;
  /** Redirect path when lacking permissions (default: /unauthorized) */
  unauthorizedPath?: string;
  /** Allow unauthenticated access (only check permissions if authenticated) */
  allowGuest?: boolean;
}

/**
 * Route wrapper that requires authentication and optionally specific permissions
 *
 * @example
 * ```tsx
 * // Require authentication only
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // Require specific permissions
 * <ProtectedRoute requiredPermissions={['borefield:create']}>
 *   <CreateBorefield />
 * </ProtectedRoute>
 *
 * // Require any of multiple permissions
 * <ProtectedRoute anyPermission={['admin:users', 'admin:settings']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  anyPermission = [],
  loginPath = '/login',
  unauthorizedPath = '/unauthorized',
  allowGuest = false,
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { hasAllPermissions, hasAnyPermission } = usePermissions();

  // Show nothing while checking auth status
  if (isLoading) {
    return null;
  }

  // Check authentication
  if (!isAuthenticated && !allowGuest) {
    // Redirect to login, preserving the intended destination
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check required permissions (all must be present)
  if (requiredPermissions.length > 0 && !hasAllPermissions(requiredPermissions)) {
    return <Navigate to={unauthorizedPath} replace />;
  }

  // Check any permission (at least one must be present)
  if (anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    return <Navigate to={unauthorizedPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
